import { candidateLabelMapping, internalLabelMapping } from "./candidate.field-label.mapping";
import type { StaticDataTypeAllowedSharedProfileTabs } from "./_generated/static-data-types";

export const NONAMETOKEN = "*NA*";
export const MAX_SELECTED_MULTI_EDIT_CANDIDATES = 100;

export function hasNoName(name: string): boolean {
  return name == null || name.trim().toLocaleUpperCase() === NONAMETOKEN;
}

export function handleNoNameCase(name: string): string {
  if (hasNoName(name)) {
    return "";
  } else {
    return name;
  }
}

export const AlwaysRequiredCandidateFields: string[] = [
  "os.profile.salutation",
  "os.profile.firstname",
  "os.profile.lastname",
];

export const RequiredCandidateFieldsExclusionList: string[] = [
  "os.profile.title",
  "os.profile.birthname",
  "os.profile.mobilePhone",
  "os.profile.immigrationCountryPhone",
  "os.profile.maritalStatusSince",
];

export const CandidateFieldsWithIsEmptyCheckbox = [
  {
    checkboxField: "os.profile.noFurtherEducationAvailable",
    field: "os.profile.furtherEducations",
  },
  {
    checkboxField: "os.profile.noExperienceAvailable",
    field: "os.profile.experiences",
  },
];

export const MedHealthCareCoreProfessionsOnlyFields = [
  "os.profile.preferredCareFacility",
  "os.profile.preferredCareFacilityFlexibility",
  "os.profile.preferredWorkingField",
  "os.profile.preferredWorkingFieldFlexibility",
];

/** The tabs the former single "education" tab was split into, one per panel of the candidate form's education tab (course performance, exams, exam results), so each can be shared and masked on its own. */
export const EDUCATION_TABS: StaticDataTypeAllowedSharedProfileTabs[] = [
  "educationPerformance",
  "educationExams",
  "educationExamResults",
] as const;

export interface CandidateTabConfiguration {
  general?: boolean;
  qualifications?: boolean;
  furtherEducations?: boolean;
  experiences?: boolean;
  languageSkills?: boolean;
  preferences?: boolean;
  vaccinations?: boolean;
  insuranceAndTax?: boolean;
  bankDetails?: boolean;
  other?: boolean;
  qualificationEvaluation?: boolean;
  healthProfessionsRegister?: boolean;
  recognitionPath?: boolean;
  laborMarketAdmission?: boolean;
  qualificationMeasure?: boolean;
  complementaryMeasure?: boolean;
  vocationalSchool?: boolean;
  visa?: boolean;
  rwrCard?: boolean;
  residence?: boolean;
  housingAcquisition?: boolean;
  processStatus?: boolean;
  processTasks?: boolean;
  processOverview?: boolean;
  internal?: boolean;
  events?: boolean;
  notes?: boolean;
  family?: boolean;
  presentation?: boolean;
  attachments?: boolean;
  requiredDocument?: boolean;
  templates?: boolean;
  cardView?: boolean;
  governance?: boolean;
  emails?: boolean;
  customFields?: boolean;
  interviews?: boolean;
  employers?: boolean;
  collections?: boolean;
  educationPerformance?: boolean;
  educationExams?: boolean;
  educationExamResults?: boolean;
  competencies?: boolean;
  onlineApplications?: boolean;
  accountingRecords?: boolean;
  externalCharacteristics?: boolean;
}

export function getFieldNameResourceKey(fieldName: string): string {
  if (fieldName.startsWith("os.profile.")) {
    return candidateLabelMapping[fieldName.replace("os.profile.", "")];
  } else if (fieldName.startsWith("internal.")) {
    return internalLabelMapping[fieldName.replace("internal.", "")];
  } else {
    throw new Error(`Unknown field name ${fieldName}`);
  }
}

export enum CandidateProfileConfigTargetType {
  Candidate = "Candidate",
  Partner = "Partner",
  User = "User",
}

/**
 * The mask/sharing tab standing for a candidate's uploaded DOCUMENTS: one of `StaticDataType.AllowedSharedProfileTabs`' keys, and the only
 * one whose "fields" are the document TYPES (`StaticDataType.AllowedUploadFileTypes`), not candidate properties. Named here because the
 * sharing editor, the mask editor's catalog and the server-side resolution into `EffectiveSharing.sharedFileTypes` all key off it.
 */
export const DOCUMENTS_TAB = "attachments";

/**
 * A whole-tab shortcut for `CandidateProfileConfigTab`/`SharedTab` that overrides per-field `tabFields`/`sharedFields`: sharing's
 * read/write distinction (`SharingTypeEnum.View` vs `.Collaboration`) at tab level, plus a stricter "must be filled in".
 *
 * Ordered `Selected < Enabled < Mandatory`, each implying the ones before (mandatory implies enabled implies visible). It applies to EVERY
 * field of the tab dynamically, later additions included, so nothing is stored per field for that tab; the editor still shows them checked
 * and read-only for clarity.
 */
export enum TabPermission {
  Selected = "Selected",
  Enabled = "Enabled",
  Mandatory = "Mandatory",
}
